import {fetchEventSource} from '@microsoft/fetch-event-source';
import axios from 'axios';

// Set the base URL for axios globally from environment variable
const jwtAxios = axios.create({
  baseURL: (process.env.REACT_APP_API_BASE_URL || 'https://be.blackdawn.info').replace(/\/$/, ''), // Remove trailing slash to prevent double slashes
  headers: {
    'Content-Type': 'application/json',
  },
});

jwtAxios.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err.response && err.response.data.msg === 'Token is not valid') {
      console.log('Need to logout user');
      // store.dispatch({type: LOGOUT});
    }
    return Promise.reject(err);
  },
);

// Function to set the authorization token for axios
export const setAuthToken = (token) => {
  if (token) {
    jwtAxios.defaults.headers.common['Authorization'] = 'Bearer ' + token;
    localStorage.setItem('token', token);
  } else {
    delete jwtAxios.defaults.headers.common['Authorization'];
    localStorage.removeItem('token');
  }
};

// Function to handle Server-Sent Events (SSE) with dynamic baseUrl support
export const sseRequest = async (path, options) => {
  // Get token from localStorage directly
  const token = localStorage.getItem('token');
  const authHeader = token ? `Bearer ${token}` : '';

  console.log('SSE Request - Token:', token ? 'Present' : 'Missing');
  console.log('SSE Request - Auth Header:', authHeader);

  // Set headers for the request, with dynamic content-type support
  let headers = options.contentType
    ? {
        Authorization: authHeader,
        Accept: 'text/event-stream',
        'content-type': options.contentType,
      }
    : {
        Authorization: authHeader,
        Accept: 'text/event-stream',
      };

  // Use dynamic baseUrl if provided in options, otherwise use environment variable
  const sseBaseUrl =
    options.baseUrl ||
    process.env.REACT_APP_API_BASE_URL ||
    'https://be.blackdawn.info'; // Use env variable or fallback to local

  // Ensure proper URL construction by removing trailing slash from baseUrl and adding single slash
  const cleanBaseUrl = sseBaseUrl.replace(/\/$/, ''); // Remove trailing slash if present
  const fullUrl = `${cleanBaseUrl}/${path}`;

  console.log('SSE Request Details:');
  console.log('- Full URL:', fullUrl);
  console.log('- Method: POST');
  console.log('- Headers:', headers);
  console.log('- Body:', options.body);

  // Perform the fetchEventSource call with the dynamic URL
  await fetchEventSource(fullUrl, {
    method: 'POST',
    headers: headers,
    body: options.body,
    onmessage: options.onmessage,
    onopen: options.onopen,
    keepalive: true,
    openWhenHidden: true,
    window,
    signal: AbortSignal.timeout(10000000), // Set timeout
    onclose: () => {
      options.onclose();
    },
    onerror: (err) => {
      console.error('SSE Error:', err);
      options.onerror(err);
      throw err; // Rethrow error to stop the operation
    },
  });
};

// Function to handle regular HTTP requests using SSE infrastructure
export const httpRequest = async (path, options = {}) => {
  return new Promise((resolve, reject) => {
    const token = localStorage.getItem('token');
    const authHeader = token ? `Bearer ${token}` : '';

    const method = options.method || 'GET';
    const baseUrl =
      options.baseUrl ||
      process.env.REACT_APP_API_BASE_URL ||
      'https://be.blackdawn.info';
    const cleanBaseUrl = baseUrl.replace(/\/$/, '');
    const fullUrl = `${cleanBaseUrl}/${path}`;

    const headers = {
      Authorization: authHeader,
      'Content-Type': options.contentType || 'application/json',
      ...options.headers,
    };

    console.log('HTTP Request Details:');
    console.log('- Full URL:', fullUrl);
    console.log('- Method:', method);
    console.log('- Headers:', headers);
    console.log('- Body:', options.body);

    // Use fetch for regular HTTP requests
    fetch(fullUrl, {
      method: method,
      headers: headers,
      body: options.body
        ? typeof options.body === 'string'
          ? options.body
          : JSON.stringify(options.body)
        : undefined,
    })
      .then(async (response) => {
        const contentType = response.headers.get('content-type');
        let data;

        if (contentType && contentType.includes('application/json')) {
          data = await response.json();
        } else {
          data = await response.text();
        }

        if (!response.ok) {
          // Create error with the backend response data
          const error = new Error(data?.message || `HTTP error! status: ${response.status}`);
          error.response = {
            data,
            status: response.status,
            statusText: response.statusText,
          };
          throw error;
        }

        resolve({
          data,
          status: response.status,
          statusText: response.statusText,
        });
      })
      .catch((error) => {
        console.error('HTTP Request Error:', error);
        reject(error);
      });
  });
};

// Export jwtAxios for other use cases
export default jwtAxios;
